import { useQueryClient } from "@tanstack/react-query";
import {
  AddOutline24Icon,
  SubtractOutline24Icon,
} from "@vygruppen/spor-icon-react";
import { getBackendUrl } from "api/common";
import { usePatchRequest } from "api/http/hooks";
import { PanelAction } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/ActionsOverflowMenu";
import { FC, FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActionModal } from "shared/components/ActionModal";
import { Button } from "shared/components/Spor";
import { Text } from "shared/components/typography/TitleStyles";
import { ReserveAlternativeTransport } from "shared/types/alternativeTransport";

type BookingRequestBody = {
  available: boolean;
};

type VehicleBookingProps = {
  vehicle: ReserveAlternativeTransport;
};

export const useVehicleBookingAction = (
  vehicle: ReserveAlternativeTransport,
): PanelAction => {
  const { t } = useTranslation();
  const { id, isAvailable } = vehicle;
  const [showModal, setShowModal] = useState(false);

  const queryClient = useQueryClient();

  const {
    patchRequest,
    patchRequestStatus: openState,
    resetStatus,
  } = usePatchRequest<string, BookingRequestBody>(
    `${getBackendUrl()}/alternativeTransports/reserve/${id}/book`,
  );

  useEffect(() => {
    if (openState.status === "success") {
      const runInvalidation = async () => {
        try {
          await queryClient.invalidateQueries({
            queryKey: ["reserveAltTransport"],
          });
          await queryClient.invalidateQueries({
            queryKey: ["backupDetails"],
          });
        } finally {
          resetStatus();
        }
      };
      void runInvalidation();
    }
  }, [openState.status, queryClient, resetStatus, isAvailable]);

  const closeModal = () => {
    setShowModal(false);
    resetStatus();
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const body: BookingRequestBody = {
      available: !isAvailable,
    };
    patchRequest(body);
  };

  return {
    key: "booking",
    label: t(isAvailable ? "Ta i bruk" : "Fristill"),
    leftIcon: isAvailable ? <AddOutline24Icon /> : <SubtractOutline24Icon />,
    buttonVariant: "primary",
    buttonSize: "sm",
    onSelect: () => setShowModal(true),
    modal: showModal ? (
      <ActionModal
        title={
          isAvailable ? "Ta i bruk reservekjøretøy" : "Fristill reservekjøretøy"
        }
        actionTitle={isAvailable ? "Ta i bruk" : "Fristill"}
        onClose={closeModal}
        onSubmit={onSubmit}
        loading={openState.status === "loading"}
        isSuccess={openState.status === "success"}
        isError={openState.status === "failure"}
        successMessage={
          isAvailable ? "Kjøretøyet ble tatt i bruk" : "Kjøretøyet ble Fristilt"
        }
        failureMessage={`Kunne ikke ${
          isAvailable ? "ta i bruk" : "fristill"
        } reservekjøretøyet. Prøv på nytt, eller kontakt IT hvis feilen vedvarer.`}
      >
        <Text>
          Bussen står som &quot;I bruk&quot; til den fristilles av kundeveileder
          på plattform.
        </Text>
        <Text>
          Husk å loggføre trasé og evt. tog og skiftnummer bussen settes inn
          for.
        </Text>
      </ActionModal>
    ) : null,
  };
};

export const VehicleBooking: FC<VehicleBookingProps> = ({ vehicle }) => {
  const action = useVehicleBookingAction(vehicle);

  return (
    <>
      <Button
        variant={action.buttonVariant}
        size={action.buttonSize}
        onClick={action.onSelect}
        leftIcon={action.leftIcon}
      >
        {action.label}
      </Button>
      {action.modal}
    </>
  );
};
